/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * MetricsPanel.tsx — localized diagnostics surface for operator telemetry.
 * It presents in-memory counters, latency percentiles, and Web Vitals and lets
 * an operator refresh the snapshot. Collection, persistence, and alerting are
 * deliberately owned by the observability services rather than this panel.
 */

/**
 * E7-S1: Dev/dashboard panel for observability metrics.
 * Shows reconnect count, presence timeout incidents, event lag / hint latency (p50/p95), Web Vitals.
 */

import { useState, useCallback } from 'react';
import { getMetrics, percentile, sortedCopy } from './metrics';
import { useTranslation } from '@/i18n';

export function MetricsPanel() {
  const t = useTranslation();
  const [snapshot, setSnapshot] = useState(() => getMetrics());

  const refresh = useCallback(() => {
    setSnapshot(getMetrics());
  }, []);

  const eventLagSorted = sortedCopy(snapshot.eventLagMs);
  const hintLatencySorted = sortedCopy(snapshot.hintLatencyMs);
  const p50 = (arr: number[]) => percentile(arr, 50);
  const p95 = (arr: number[]) => percentile(arr, 95);

  return (
    <section className="rounded-lg border p-3 text-sm glass-panel" aria-label={t('metrics.label')}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-medium" style={{ color: 'var(--text-primary)' }}>
          {t('metrics.title')}
        </h3>
        <div className="flex items-center gap-2">
          {import.meta.env.VITE_OPERATOR_FEEDBACK_URL && (
            <a
              href={import.meta.env.VITE_OPERATOR_FEEDBACK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-sky-600 dark:text-sky-400 hover:underline"
            >
              {t('metrics.feedback')}
            </a>
          )}
          <button
            type="button"
            onClick={refresh}
            className="text-xs text-sky-600 dark:text-sky-400 hover:underline"
          >
            {t('metrics.refresh')}
          </button>
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1" style={{ color: 'var(--text-secondary)' }}>
        <dt>{t('metrics.reconnect_total')}</dt>
        <dd className="font-mono">{snapshot.reconnectTotal}</dd>
        <dt>{t('metrics.presence_timeout')}</dt>
        <dd className="font-mono">{snapshot.presenceTimeoutIncidentTotal}</dd>
        <dt className="col-span-2 mt-1 font-medium" style={{ color: 'var(--text-secondary)' }}>
          {t('metrics.pilot_hints')}
        </dt>
        <dt>{t('metrics.hints_received')}</dt>
        <dd className="font-mono">{snapshot.hintReceivedTotal}</dd>
        <dt>{t('metrics.hints_used')}</dt>
        <dd className="font-mono">{snapshot.hintMarkedUsedTotal}</dd>
        <dt>{t('metrics.hints_irrelevant')}</dt>
        <dd className="font-mono">{snapshot.hintMarkedIrrelevantTotal}</dd>
        <dt>{t('metrics.adoption')}</dt>
        <dd className="font-mono">
          {snapshot.hintMarkedUsedTotal + snapshot.hintMarkedIrrelevantTotal > 0
            ? Math.round(
                (snapshot.hintMarkedUsedTotal /
                  (snapshot.hintMarkedUsedTotal + snapshot.hintMarkedIrrelevantTotal)) *
                  100
              )
            : '—'}
          %
        </dd>
        <dt>{t('metrics.event_lag')}</dt>
        <dd className="font-mono">
          {p50(eventLagSorted) != null
            ? `${Math.round(p50(eventLagSorted)!)} / ${Math.round(p95(eventLagSorted) ?? 0)}`
            : '—'}
        </dd>
        <dt>{t('metrics.hint_latency')}</dt>
        <dd className="font-mono">
          {p50(hintLatencySorted) != null
            ? `${Math.round(p50(hintLatencySorted)!)} / ${Math.round(p95(hintLatencySorted) ?? 0)}`
            : '—'}
        </dd>
        <dt>{t('metrics.event_samples')}</dt>
        <dd className="font-mono">{snapshot.eventLagMs.length}</dd>
        <dt>{t('metrics.hint_samples')}</dt>
        <dd className="font-mono">{snapshot.hintLatencyMs.length}</dd>
      </dl>
      {Object.keys(snapshot.webVitals).length > 0 && (
        <>
          <h4 className="mt-2 font-medium" style={{ color: 'var(--text-secondary)' }}>
            Web Vitals
          </h4>
          <dl
            className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1"
            style={{ color: 'var(--text-secondary)' }}
          >
            {snapshot.webVitals.cls != null && (
              <>
                <dt>CLS</dt>
                <dd className="font-mono">{snapshot.webVitals.cls.toFixed(3)}</dd>
              </>
            )}
            {snapshot.webVitals.fcp != null && (
              <>
                <dt>{t('metrics.fcp')}</dt>
                <dd className="font-mono">{Math.round(snapshot.webVitals.fcp)}</dd>
              </>
            )}
            {snapshot.webVitals.inp != null && (
              <>
                <dt>{t('metrics.inp')}</dt>
                <dd className="font-mono">{Math.round(snapshot.webVitals.inp)}</dd>
              </>
            )}
            {snapshot.webVitals.lcp != null && (
              <>
                <dt>{t('metrics.lcp')}</dt>
                <dd className="font-mono">{Math.round(snapshot.webVitals.lcp)}</dd>
              </>
            )}
            {snapshot.webVitals.ttfb != null && (
              <>
                <dt>{t('metrics.ttfb')}</dt>
                <dd className="font-mono">{Math.round(snapshot.webVitals.ttfb)}</dd>
              </>
            )}
          </dl>
        </>
      )}
    </section>
  );
}
